import { NavPoint, Room } from "@office-map/api-schema";

import { floors } from "../storage/data-provider";

import { getCost } from "./navigation.utils";

export const isPointInRoom = (point: { x: number; y: number }, room: Room) => {
    // Transform point to room's local coordinate system
    const localX = point.x - room.shape.origin[0];
    const localY = point.y - room.shape.origin[1];

    // Ray casting algorithm using local coordinates
    const polygon = room.shape.points;
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i][0];
        const yi = polygon[i][1];
        const xj = polygon[j][0];
        const yj = polygon[j][1];

        const intersect = yi > localY !== yj > localY && localX < ((xj - xi) * (localY - yi)) / (yj - yi) + xi;
        if (intersect) inside = !inside;
    }
    return inside;
};

export const findClosestNavPoint = (targetId: string, navPoints: NavPoint[], rooms: Room[]): NavPoint | null => {
    // Check if the target point exists in NavPoints
    const existingPoint = navPoints.find((p) => p.id === targetId);

    if (existingPoint) {
        return existingPoint;
    }

    // Find the room
    const room = rooms.find((r) => r.id === targetId);
    if (!room) {
        return null; // Or handle as error/undefined
    }
    const roomFloorId = floors.find((f) => f.roomIds.includes(room.id))!.id;
    const floorRoomIds = floors.find((f) => f.id === roomFloorId)?.roomIds;

    // 1. Check if there is any NavPoint inside the target room
    const pointInsideRoom = navPoints.find((p) => p.floorId === roomFloorId && isPointInRoom(p.location, room));

    if (pointInsideRoom) {
        return pointInsideRoom;
    }

    // Find nearest nav point
    // Calculate distance to all nav points and pick the minimum
    let minDistance = Infinity;
    let closestPoint: NavPoint | null = null;

    const roomPoints = [
        room.shape.origin,
        ...room.shape.points.map((p) => [room.shape.origin[0] + p[0], room.shape.origin[1] + p[1]] as [number, number]),
    ];

    navPoints
        .filter((p) => p.floorId === roomFloorId)
        .forEach((point) => {
            // Skip points that are inside any room
            if (rooms.some((r) => floorRoomIds?.includes(r.id) && isPointInRoom(point.location, r))) {
                return;
            }

            // Skip stair points that are used for floor connection
            // We assume "stairNavPointId" is the ID of the stair nav point
            const isStair = floors.some((f) => f.stairNavPointId === point.id);
            if (isStair) return;

            // Check distance to all room points
            let sumRoomDist = 0;
            for (const rp of roomPoints) {
                const dist = getCost(point.location, { x: rp[0], y: rp[1] });
                sumRoomDist += dist;
            }
            const avgRoomDist = sumRoomDist / roomPoints.length;

            if (avgRoomDist < minDistance) {
                minDistance = avgRoomDist;
                closestPoint = point;
            }
        });

    return closestPoint;
};
